/**
 * How a measurement axis follows its bounding box.
 *
 * A seeded box's landmarks are only useful while they describe the object under
 * the box, so moving or resizing the box has to carry them along. Keeping this
 * pure — no annotation, no React, no depth — means the rule is testable on its
 * own and the same transform can drive both the live drag preview and the
 * committed edit.
 */
import type { BBoxPixels } from "@/types/annotations";

export type Point = { x: number; y: number };
export type Axis = { start: Point; end: Point };

export type BoxChange = { kind: "translate"; dx: number; dy: number } | { kind: "resize"; next: BBoxPixels };

/** Below this, an axis is too short to measure and gets re-seeded instead. */
const MIN_AXIS_SEPARATION_PX = 1e-6;

/**
 * Classify a box edit. Unchanged dimensions mean the box moved rigidly, which
 * is the case where the axis geometry — and therefore its measured value —
 * survives untouched. Returns null when nothing moved.
 */
export function classifyBoxChange(previous: BBoxPixels, next: BBoxPixels): BoxChange | null {
  const sameSize = previous.width === next.width && previous.height === next.height;
  const dx = next.x - previous.x;
  const dy = next.y - previous.y;

  if (sameSize) {
    return dx === 0 && dy === 0 ? null : { kind: "translate", dx, dy };
  }
  return { kind: "resize", next };
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const clampIntoBox = (point: Point, box: BBoxPixels): Point => ({
  x: clamp(point.x, box.x, box.x + box.width),
  y: clamp(point.y, box.y, box.y + box.height),
});

/**
 * Map one axis through a box change.
 *
 * A translate shifts both endpoints by the delta and never clamps: endpoint
 * dragging clamps to the image rather than the box, so a landmark parked
 * outside its box is a deliberate choice to preserve.
 *
 * A resize clamps each endpoint into the new box per coordinate, so a landmark
 * still inside is returned untouched. If clamping would collapse the axis onto a
 * single point — a box shrunk into a far corner — the whole axis falls back to
 * its default placement, because a zero-length axis cannot be measured and is
 * silently dropped by the save payload. The fallback is per axis, not per point,
 * so axes that share a base point stay coincident.
 */
export function transformAxisForBoxChange(
  axis: Axis,
  change: BoxChange,
  fallback: Axis,
  imageBounds?: { width: number; height: number }
): Axis {
  if (change.kind === "translate") {
    // Clamp to the IMAGE, never the box: a landmark parked outside its box is a
    // deliberate choice, but one pushed outside the image is rejected outright
    // by both save channels and would make the whole capture unsaveable.
    const shift = (point: Point): Point => {
      const moved = { x: point.x + change.dx, y: point.y + change.dy };
      return imageBounds
        ? { x: clamp(moved.x, 0, imageBounds.width), y: clamp(moved.y, 0, imageBounds.height) }
        : moved;
    };
    const start = shift(axis.start);
    const end = shift(axis.end);
    // Clamping each endpoint independently can map both onto the same pixel at
    // an image edge. A zero-length axis cannot be measured, so re-seed it just
    // as the resize branch does rather than persist a degenerate pair.
    if (Math.hypot(end.x - start.x, end.y - start.y) <= MIN_AXIS_SEPARATION_PX) {
      return { start: { ...fallback.start }, end: { ...fallback.end } };
    }
    return { start, end };
  }

  const start = clampIntoBox(axis.start, change.next);
  const end = clampIntoBox(axis.end, change.next);
  if (Math.hypot(end.x - start.x, end.y - start.y) <= MIN_AXIS_SEPARATION_PX) {
    return { start: { ...fallback.start }, end: { ...fallback.end } };
  }
  return { start, end };
}
